import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  User,
  Phone,
  MapPin,
  Calendar,
  ShieldCheck,
  Edit3,
  Trash2,
  Building2,
  Briefcase,
  Tag,
  Users,
  GraduationCap,
  Award,
  School,
  Hash,
  StickyNote,
  GitMerge,
  Landmark,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { formatDateVN } from "../../../utils/formatUtils";
import { hasPermission } from "../../../utils/permissionUtils";
import { confirmDeleteCustomer } from "./confirmCustomerDelete";

const statusLabelMap = {
  new: "Mới",
  active: "Đang chăm sóc",
  idle: "Không tương tác",
  lost: "Mất khách",
};

const statusClassMap = {
  new: "bg-blue-50 border-blue-100 text-blue-600",
  active: "bg-green-50 border-green-100 text-green-600",
  idle: "bg-amber-50 border-amber-100 text-amber-600",
  lost: "bg-gray-50 border-gray-100 text-gray-500",
};

const recordStatusLabelMap = {
  active: "Đang hoạt động",
  closed: "Đã đóng",
  merged: "Đã sáp nhập",
};

const recordStatusClassMap = {
  active: "bg-emerald-50 border-emerald-100 text-emerald-700",
  closed: "bg-slate-100 border-slate-200 text-slate-600",
  merged: "bg-violet-50 border-violet-100 text-violet-700",
};

const leadSourceLabelMap = {
  Facebook: "Facebook",
  Website: "Website",
  TikTok: "TikTok",
  Referral: "Giới thiệu",
  Event: "Workshop / Sự kiện",
};

const SectionTitle = ({ children }) => (
  <h3 className="mb-2.5 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-red-500">
    <span className="h-4 w-1 rounded-full bg-red-500" />
    {children}
  </h3>
);

const InfoItem = ({
  icon: Icon,
  label,
  value,
  iconColor = "text-gray-400",
  multiline = false,
}) => (
  <div className="flex min-w-0 items-start gap-2.5 rounded-xl border border-transparent p-2 transition-colors hover:border-gray-100 hover:bg-gray-50">
    <div className={`shrink-0 rounded-lg bg-gray-50 p-2 ${iconColor}`}>
      <Icon size={16} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-bold uppercase tracking-tight text-gray-400">
        {label}
      </p>
      <p
        className={`text-[13px] font-semibold text-gray-700 ${
          multiline ? "whitespace-pre-line break-words" : "truncate"
        }`}
      >
        {value || "Chưa cập nhật"}
      </p>
    </div>
  </div>
);

const CustomerDetailModal = ({ isOpen, customer, onClose, onEdit, onDelete }) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEsc);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, onClose]);

  if (!customer) return null;

  const linkedClassCount = Number(customer.linked_class_count || 0);
  const linkedStudentCount = Number(customer.linked_student_count || 0);
  const issuedCertificateCount = Number(customer.issued_certificate_count || 0);
  const isSchoolPartner = customer.customer_type === "Trường học";
  const hasLinkedClasses = isSchoolPartner && linkedClassCount > 0;
  const studentCount =
    customer.student_count != null && customer.student_count !== ""
      ? Number(customer.student_count)
      : null;

  const elearningStats = [
    {
      key: "classes",
      label: "Lớp học",
      value: linkedClassCount,
      Icon: GraduationCap,
      iconClass: "text-indigo-600 bg-indigo-50",
    },
    {
      key: "students",
      label: "Học viên",
      value: linkedStudentCount,
      Icon: Users,
      iconClass: "text-emerald-600 bg-emerald-50",
    },
    {
      key: "certificates",
      label: "Chứng nhận",
      value: issuedCertificateCount,
      Icon: Award,
      iconClass: "text-amber-600 bg-amber-50",
    },
  ];

  const handleDeleteClick = async () => {
    const confirm = await confirmDeleteCustomer(customer);
    if (confirm.isConfirmed) {
      onDelete?.(customer.id);
    }
  };

  const leadSourceLabel =
    leadSourceLabelMap[customer.lead_source] ||
    customer.lead_source ||
    "Chưa cập nhật";
  const statusLabel = statusLabelMap[customer.status] || "Chưa cập nhật";
  const statusClass =
    statusClassMap[customer.status] || "bg-gray-50 border-gray-100 text-gray-500";
  const recordStatus = customer.record_status || "active";
  const recordStatusLabel =
    recordStatusLabelMap[recordStatus] || "Đang hoạt động";
  const recordStatusClass =
    recordStatusClassMap[recordStatus] ||
    "bg-gray-50 border-gray-100 text-gray-500";
  const profileSubtitle = [customer.customer_type, customer.school_level]
    .filter(Boolean)
    .join(" · ");
  const locationLine = [customer.ward, customer.address]
    .filter(Boolean)
    .join(" · ");
  const showLifecycle = recordStatus !== "active";

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-slate-900/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="pointer-events-none fixed inset-0 z-[210] flex items-end justify-center p-0 sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              onClick={(e) => e.stopPropagation()}
              className="pointer-events-auto flex h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
            >
              <div className="flex shrink-0 justify-center pt-2 sm:hidden" aria-hidden>
                <span className="h-1 w-10 rounded-full bg-gray-200" />
              </div>

              {/* Header */}
              <div className="relative flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-4 pb-3 pt-2 sm:items-center sm:p-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-red-100 bg-red-50 text-xl font-black text-red-600 shadow-sm sm:h-16 sm:w-16 sm:text-2xl">
                    {customer.name?.charAt(0)?.toUpperCase() || "K"}
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-bold text-gray-800 sm:text-xl">
                      {customer.name || "Khách hàng"}
                    </h2>
                    {profileSubtitle ? (
                      <p className="mt-0.5 truncate text-xs font-medium text-gray-500">
                        {profileSubtitle}
                        {isSchoolPartner && Number.isFinite(studentCount) ? (
                          <span className="text-gray-400">
                            {" "}
                            · ~{studentCount} học sinh
                          </span>
                        ) : null}
                      </p>
                    ) : null}
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span
                        className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase ${statusClass}`}
                      >
                        {statusLabel}
                      </span>
                      <span
                        className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase ${recordStatusClass}`}
                      >
                        {recordStatusLabel}
                      </span>
                      {customer.branch_name ? (
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase text-gray-500">
                          {customer.branch_name}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-400 transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:p-6">
                <div className="grid grid-cols-1 gap-5 sm:gap-6 lg:grid-cols-2">
                  {/* Cột trái */}
                  <div className="space-y-4">
                    <div>
                      <SectionTitle>Hồ sơ khách hàng</SectionTitle>
                      <div className="grid grid-cols-1 gap-0.5 rounded-2xl border border-gray-100 bg-gray-50/40 p-1.5 sm:grid-cols-2 sm:p-2">
                        <InfoItem
                          icon={Phone}
                          label="Số điện thoại"
                          value={customer.phone}
                          iconColor="text-red-600"
                        />
                        <InfoItem
                          icon={User}
                          label="Loại khách hàng"
                          value={customer.customer_type}
                          iconColor="text-purple-500"
                        />
                        {isSchoolPartner || customer.school_level ? (
                          <InfoItem
                            icon={School}
                            label="Cấp học"
                            value={customer.school_level}
                            iconColor="text-indigo-600"
                          />
                        ) : null}
                        {isSchoolPartner || Number.isFinite(studentCount) ? (
                          <InfoItem
                            icon={Hash}
                            label="Quy mô học sinh"
                            value={
                              Number.isFinite(studentCount)
                                ? `~${studentCount} học sinh`
                                : null
                            }
                            iconColor="text-emerald-600"
                          />
                        ) : null}
                        <InfoItem
                          icon={ShieldCheck}
                          label="Trạng thái chăm sóc"
                          value={statusLabel}
                          iconColor="text-green-600"
                        />
                      </div>
                    </div>

                    <div>
                      <SectionTitle>Địa bàn</SectionTitle>
                      <div className="grid grid-cols-1 gap-0.5 rounded-2xl border border-gray-100 bg-gray-50/40 p-1.5 sm:p-2">
                        <InfoItem
                          icon={Building2}
                          label="Chi nhánh phụ trách"
                          value={customer.branch_name}
                          iconColor="text-blue-500"
                        />
                        <InfoItem
                          icon={MapPin}
                          label="Phường / Xã"
                          value={customer.ward}
                          iconColor="text-orange-500"
                        />
                        <InfoItem
                          icon={MapPin}
                          label="Địa chỉ"
                          value={locationLine || customer.address}
                          iconColor="text-orange-500"
                          multiline
                        />
                      </div>
                    </div>

                    <div>
                      <SectionTitle>Thống kê E-Learning</SectionTitle>
                      <div className="grid grid-cols-3 gap-2">
                        {elearningStats.map(
                          ({ key, label, value, Icon, iconClass }) => (
                            <div
                              key={key}
                              className="rounded-2xl border border-gray-100 bg-gray-50/80 p-2.5 text-center sm:p-3"
                            >
                              <div
                                className={`mx-auto mb-1.5 flex h-8 w-8 items-center justify-center rounded-xl sm:mb-2 sm:h-9 sm:w-9 ${iconClass}`}
                              >
                                <Icon size={16} />
                              </div>
                              <p className="text-base font-black text-gray-800 sm:text-lg">
                                {value}
                              </p>
                              <p className="mt-0.5 text-[9px] font-bold uppercase tracking-tight text-gray-400 sm:text-[10px]">
                                {label}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    </div>

                    {hasLinkedClasses ? (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3.5 sm:p-4">
                        <p className="text-xs font-semibold leading-relaxed text-amber-900">
                          Trường này đang gắn {linkedClassCount} lớp học. Nếu xóa
                          trường, các lớp vẫn còn trong hệ thống nhưng sẽ không còn
                          liên kết với trường này.
                        </p>
                      </div>
                    ) : null}
                  </div>

                  {/* Cột phải */}
                  <div className="space-y-4">
                    <div>
                      <SectionTitle>Thông tin CRM</SectionTitle>
                      <div className="grid grid-cols-1 gap-0.5 rounded-2xl border border-gray-100 bg-gray-50/50 p-1.5 sm:grid-cols-2 sm:p-2">
                        <InfoItem
                          icon={Briefcase}
                          label="Nguồn khách"
                          value={leadSourceLabel}
                          iconColor="text-indigo-600"
                        />
                        <InfoItem
                          icon={Users}
                          label="Sales phụ trách"
                          value={customer.owner_name}
                          iconColor="text-emerald-600"
                        />
                        <InfoItem
                          icon={Calendar}
                          label="Ngày tạo hồ sơ"
                          value={formatDateVN(customer.created_at)}
                          iconColor="text-blue-500"
                        />
                        <InfoItem
                          icon={Calendar}
                          label="Cập nhật gần nhất"
                          value={formatDateVN(customer.updated_at)}
                          iconColor="text-gray-500"
                        />
                      </div>
                    </div>

                    <div>
                      <SectionTitle>Tình trạng hoạt động</SectionTitle>
                      <div
                        className={`rounded-2xl border p-3.5 sm:p-4 ${
                          showLifecycle
                            ? recordStatus === "merged"
                              ? "border-violet-200 bg-violet-50/70"
                              : "border-slate-200 bg-slate-50"
                            : "border-gray-100 bg-gray-50/50"
                        }`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase ${recordStatusClass}`}
                          >
                            <GitMerge size={12} />
                            {recordStatusLabel}
                          </span>
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-0.5 sm:grid-cols-2">
                          {recordStatus === "merged" ? (
                            <InfoItem
                              icon={GitMerge}
                              label="Hồ sơ kế thừa"
                              value={customer.successor_customer_name}
                              iconColor="text-violet-600"
                              multiline
                            />
                          ) : null}
                          {showLifecycle ? (
                            <InfoItem
                              icon={Calendar}
                              label="Ngày đóng / sáp nhập"
                              value={formatDateVN(customer.closed_at)}
                              iconColor="text-slate-600"
                            />
                          ) : null}
                        </div>

                        {showLifecycle && customer.closed_note ? (
                          <div className="mt-2 rounded-xl border border-white/60 bg-white/70 p-3">
                            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                              Ghi chú đóng / sáp nhập
                            </p>
                            <p className="mt-1 whitespace-pre-line text-xs font-medium leading-relaxed text-gray-700">
                              {customer.closed_note}
                            </p>
                          </div>
                        ) : null}

                        {!showLifecycle ? (
                          <p className="mt-2 text-xs text-gray-500">
                            Hồ sơ đang hoạt động bình thường trong CRM.
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div>
                      <SectionTitle>Tags</SectionTitle>
                      <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-3.5 sm:p-4">
                        {customer.tags?.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {customer.tags.map((tag) => (
                              <span
                                key={tag.id}
                                className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-bold"
                                style={{
                                  color: tag.color || "#374151",
                                  backgroundColor: `${tag.color || "#e5e7eb"}20`,
                                  borderColor: `${tag.color || "#e5e7eb"}33`,
                                }}
                              >
                                <Tag size={12} className="mr-1" />
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm italic text-gray-500">Chưa có tag</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <SectionTitle>Người liên hệ</SectionTitle>
                      <div className="space-y-2">
                        {customer.representatives?.length > 0 ? (
                          customer.representatives.map((rep, index) => {
                            const hasBank =
                              rep.bank_name ||
                              rep.bank_branch ||
                              rep.bank_account_name ||
                              rep.bank_account_number;
                            return (
                              <div
                                key={rep.id || `${rep.name || "rep"}-${index}`}
                                className="rounded-2xl border border-gray-100 bg-red-50/40 p-3.5 sm:p-4"
                              >
                                <div className="font-bold text-gray-800">
                                  {rep.name || "Chưa có tên"}
                                </div>
                                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-600">
                                  <span className="inline-flex items-center gap-1">
                                    <Phone size={12} className="text-red-500" />
                                    {rep.phone || "Chưa có SĐT"}
                                  </span>
                                  {rep.position ? (
                                    <span className="inline-flex items-center gap-1 text-gray-500">
                                      <Briefcase size={12} />
                                      {rep.position}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-400">
                                      Chưa có chức vụ
                                    </span>
                                  )}
                                </div>
                                {hasBank ? (
                                  <div className="mt-2.5 rounded-xl border border-red-100/80 bg-white/70 p-2.5 text-[11px] text-gray-600">
                                    <div className="mb-1 flex items-center gap-1 font-bold uppercase tracking-wide text-gray-400">
                                      <Landmark size={12} />
                                      Tài khoản ngân hàng
                                    </div>
                                    <div className="space-y-0.5">
                                      {rep.bank_name ? (
                                        <p>
                                          <span className="font-semibold text-gray-500">
                                            Ngân hàng:{" "}
                                          </span>
                                          {rep.bank_name}
                                          {rep.bank_branch
                                            ? ` · ${rep.bank_branch}`
                                            : ""}
                                        </p>
                                      ) : null}
                                      {rep.bank_account_name ? (
                                        <p>
                                          <span className="font-semibold text-gray-500">
                                            Chủ TK:{" "}
                                          </span>
                                          {rep.bank_account_name}
                                        </p>
                                      ) : null}
                                      {rep.bank_account_number ? (
                                        <p>
                                          <span className="font-semibold text-gray-500">
                                            Số TK:{" "}
                                          </span>
                                          {rep.bank_account_number}
                                        </p>
                                      ) : null}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })
                        ) : (
                          <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-3.5 text-sm italic text-gray-500 sm:p-4">
                            Chưa có người liên hệ
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <SectionTitle>Ghi chú nội bộ</SectionTitle>
                      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-3.5 sm:p-4">
                        <div className="flex gap-3">
                          <StickyNote
                            className="shrink-0 text-blue-500"
                            size={18}
                          />
                          <p className="whitespace-pre-line text-xs font-medium leading-relaxed text-blue-700">
                            {customer.note || "Chưa có ghi chú nội bộ."}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:gap-3 sm:bg-gray-50/30 sm:p-6 sm:pb-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl px-4 py-2.5 text-sm font-bold text-gray-500 transition-all hover:bg-gray-100 sm:px-6 sm:py-3"
                >
                  ĐÓNG
                </button>

                <div className="flex flex-wrap justify-end gap-2 sm:gap-3">
                  {hasPermission("crm.customers", "D") && (
                    <button
                      type="button"
                      onClick={handleDeleteClick}
                      className="flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3.5 py-2.5 text-sm font-bold text-red-600 transition-all hover:bg-red-50 active:scale-[0.98] sm:gap-2 sm:px-6 sm:py-3"
                    >
                      <Trash2 size={16} /> Xóa
                    </button>
                  )}

                  {hasPermission("crm.customers", "U") && (
                    <button
                      type="button"
                      onClick={() => onEdit?.(customer.id)}
                      className="flex items-center gap-1.5 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-red-100 transition-all hover:bg-red-700 active:scale-95 sm:gap-2 sm:px-8 sm:py-3"
                    >
                      <Edit3 size={16} /> CHỈNH SỬA
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CustomerDetailModal;
